"use client";

import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import {
  Globe,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  BookOpen,
  Plus,
  Trash2,
  X,
  Check,
  Pencil,
  Link2,
  Music,
  Users,
  Calendar,
  MapPin,
  Headphones,
  RefreshCw,
  EyeOff,
  Eye,
  Search,
} from "lucide-react";
import {
  getEntityWebLinks,
  saveEntityWebLinks,
  type CustomWebLink,
} from "@/services/web-search";

// ── Types from /api/artist-info ───────────────────────────────────────────────

interface WikipediaInfo {
  title: string; url: string; extract: string;
  thumbnail: { source: string; width: number; height: number } | null;
  categories: string[]; found: boolean;
}
interface MBUrl { type: string; url: string; }
interface MBMember { name: string; ended: boolean; begin?: string | null; end?: string | null; }
interface MusicBrainzInfo {
  id: string; name: string; type: string | null; country: string | null; area: string | null;
  beginDate: string | null; endDate: string | null; disambiguation: string | null;
  tags: string[]; genres: string[]; aliases: string[];
  urls: MBUrl[]; members: MBMember[]; mbUrl: string; found: boolean;
}
interface DiscogsImage { uri: string; type: string; }
interface DiscogsInfo {
  id: number; name: string; url: string; profile: string; urls: string[];
  images: DiscogsImage[]; members: { name: string; active: boolean }[] | null;
  namevariations: string[]; dataQuality: string | null; found: boolean;
}
interface LastFmSimilar { name: string; url: string; image: string; }
interface LastFmInfo {
  url: string; listeners: number; playcount: number; bio: string;
  tags: string[]; similar: LastFmSimilar[]; found: boolean;
}
interface ArtistInfoData {
  name: string;
  wikipedia: WikipediaInfo | null;
  musicbrainz: MusicBrainzInfo | null;
  discogs: DiscogsInfo | null;
  lastfm: LastFmInfo | null;
}

// ── URL → platform helper ─────────────────────────────────────────────────────

function platformFromUrl(url: string): { name: string; color: string } {
  if (url.includes('open.spotify.com') || url.includes('spotify.com')) return { name: 'Spotify', color: 'text-green-500' };
  if (url.includes('youtube.com') || url.includes('youtu.be'))          return { name: 'YouTube', color: 'text-red-500' };
  if (url.includes('soundcloud.com'))  return { name: 'SoundCloud', color: 'text-orange-500' };
  if (url.includes('bandcamp.com'))    return { name: 'Bandcamp', color: 'text-teal-500' };
  if (url.includes('facebook.com'))    return { name: 'Facebook', color: 'text-blue-600' };
  if (url.includes('instagram.com'))   return { name: 'Instagram', color: 'text-pink-500' };
  if (url.includes('twitter.com') || url.includes('x.com'))             return { name: 'X / Twitter', color: 'text-sky-400' };
  if (url.includes('allmusic.com'))    return { name: 'AllMusic', color: 'text-blue-400' };
  if (url.includes('discogs.com'))     return { name: 'Discogs', color: 'text-amber-500' };
  if (url.includes('imdb.com'))        return { name: 'IMDb', color: 'text-yellow-400' };
  if (url.includes('last.fm') || url.includes('lastfm.com')) return { name: 'Last.fm', color: 'text-red-400' };
  if (url.includes('musicbrainz.org')) return { name: 'MusicBrainz', color: 'text-violet-500' };
  if (url.includes('wikidata.org'))    return { name: 'Wikidata', color: 'text-green-600' };
  if (url.includes('genius.com'))      return { name: 'Genius', color: 'text-yellow-300' };
  if (url.includes('music.apple.com')) return { name: 'Apple Music', color: 'text-pink-400' };
  if (url.includes('tidal.com'))       return { name: 'Tidal', color: 'text-cyan-500' };
  if (url.includes('deezer.com'))      return { name: 'Deezer', color: 'text-purple-500' };
  if (url.includes('bbc.'))            return { name: 'BBC', color: 'text-red-600' };
  if (url.includes('amazon.com') || url.includes('music.amazon')) return { name: 'Amazon Music', color: 'text-yellow-500' };
  if (url.includes('beatport.com'))    return { name: 'Beatport', color: 'text-emerald-500' };
  if (url.includes('mixcloud.com'))    return { name: 'Mixcloud', color: 'text-indigo-400' };
  if (url.includes('tiktok.com'))      return { name: 'TikTok', color: 'text-fuchsia-400' };
  if (url.includes('vimeo.com'))       return { name: 'Vimeo', color: 'text-blue-500' };
  try {
    return { name: new URL(url).hostname.replace(/^www\./, ''), color: 'text-muted-foreground' };
  } catch {
    return { name: 'Link', color: 'text-muted-foreground' };
  }
}

function fmtNum(n: number) {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000)     return (n / 1_000).toFixed(0) + 'K';
  return n.toString();
}

// ── Card: Wikipedia ───────────────────────────────────────────────────────────

function WikipediaCard({ data }: { data: WikipediaInfo }) {
  const [expanded, setExpanded] = useState(false);
  const PREVIEW = 800;
  const fullText = data.extract || '';
  const preview = fullText.length > PREVIEW ? fullText.slice(0, PREVIEW) + '…' : fullText;

  return (
    <div className="rounded-xl border border-border/60 bg-card/40 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-2.5 bg-blue-500/8 border-b border-border/30">
        <BookOpen className="h-4 w-4 text-blue-400 flex-shrink-0" />
        <span className="text-sm font-semibold flex-grow truncate text-blue-200">{data.title}</span>
        <a href={data.url} target="_blank" rel="noopener noreferrer" aria-label="Open the full article on Wikipedia"
          className="text-xs text-muted-foreground hover:text-primary flex items-center gap-0.5 flex-shrink-0">
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>

      {/* Body */}
      <div className="p-4 flex gap-4">
        {data.thumbnail && (
          <div className="flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden border border-border/40">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={data.thumbnail.source} alt={data.title} className="w-full h-full object-cover" />
          </div>
        )}
        <div className="flex-1 min-w-0 space-y-2">
          <p className="text-sm leading-relaxed text-foreground/85 whitespace-pre-line">
            {expanded ? fullText : preview}
          </p>
          {fullText.length > PREVIEW && (
            <Button variant="ghost" size="sm" className="h-6 text-xs text-primary px-0 hover:bg-transparent"
              onClick={() => setExpanded(!expanded)}>
              {expanded ? <><ChevronUp className="h-3 w-3 mr-1" />Show less</> : <><ChevronDown className="h-3 w-3 mr-1" />Read more</>}
            </Button>
          )}
        </div>
      </div>

      {/* Categories */}
      {data.categories.length > 0 && (
        <div className="px-4 pb-3 flex flex-wrap gap-1">
          {data.categories.map(c => (
            <span key={c} className="text-[10px] px-1.5 py-0.5 rounded bg-secondary/40 text-muted-foreground">{c}</span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Card: MusicBrainz ────────────────────────────────────────────────────────

function MusicBrainzCard({ data }: { data: MusicBrainzInfo }) {
  const allTags = [...new Set([...data.genres, ...data.tags])].slice(0, 25);

  // Group URLs by kind
  const urlChips = data.urls
    .filter(u => !u.url.includes('wikipedia.org') && !u.url.includes('wikidata.org'))
    .map(u => ({ ...platformFromUrl(u.url), url: u.url, mbType: u.type }));

  // Deduplicate by platform name
  const seen = new Set<string>();
  const uniqueChips = urlChips.filter(c => {
    if (seen.has(c.name)) return false;
    seen.add(c.name);
    return true;
  });

  return (
    <div className="rounded-xl border border-border/60 bg-card/40 overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 bg-violet-500/8 border-b border-border/30">
        <Music className="h-4 w-4 text-violet-400 flex-shrink-0" />
        <span className="text-sm font-semibold flex-grow text-violet-200">MusicBrainz</span>
        {data.type && <Badge variant="outline" className="text-[10px] h-5 border-violet-400/40 text-violet-300">{data.type}</Badge>}
        <a href={data.mbUrl} target="_blank" rel="noopener noreferrer" aria-label="Open on MusicBrainz"
          className="text-xs text-muted-foreground hover:text-primary flex items-center gap-0.5 flex-shrink-0">
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>

      <div className="p-4 space-y-3">
        {/* Meta row */}
        <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-muted-foreground">
          {(data.country || data.area) && (
            <span className="flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5 text-violet-400" />
              {[data.area, data.country].filter(Boolean).join(', ')}
            </span>
          )}
          {data.beginDate && (
            <span className="flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5 text-violet-400" />
              {data.type === 'Group' ? 'Formed' : 'Born'}: {data.beginDate}
              {data.endDate ? ` — ${data.endDate}` : ''}
            </span>
          )}
          {data.disambiguation && (
            <span className="text-muted-foreground/70 italic">{data.disambiguation}</span>
          )}
        </div>

        {/* Genres / Tags */}
        {allTags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {allTags.map(t => (
              <Badge key={t} variant="secondary" className="text-[11px] h-5 font-normal">{t}</Badge>
            ))}
          </div>
        )}

        {/* Aliases / name variations */}
        {data.aliases.length > 0 && (
          <p className="text-xs text-muted-foreground">
            <span className="font-medium">Also known as: </span>{data.aliases.join(' · ')}
          </p>
        )}

        {/* Members */}
        {data.members.length > 0 && (
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
              <Users className="h-3.5 w-3.5" /> Members
            </p>
            <div className="flex flex-wrap gap-1.5">
              {data.members.map((m, i) => (
                <span key={i} className={`text-xs px-2 py-0.5 rounded-full border ${m.ended ? 'border-border/40 text-muted-foreground opacity-60' : 'border-violet-400/40 text-violet-300'}`}>
                  {m.name}{m.ended ? ' (former)' : ''}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* External links from MB URL relations */}
        {uniqueChips.length > 0 && (
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">Links</p>
            <div className="flex flex-wrap gap-1.5">
              {uniqueChips.map((c, i) => (
                <a key={i} href={c.url} target="_blank" rel="noopener noreferrer">
                  <Badge variant="outline" className={`cursor-pointer hover:bg-secondary transition-colors gap-1 text-xs ${c.color}`}>
                    <ExternalLink className="h-3 w-3" />{c.name}
                  </Badge>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Card: Discogs ─────────────────────────────────────────────────────────────

function DiscogsCard({ data }: { data: DiscogsInfo }) {
  const [expanded, setExpanded] = useState(false);
  const PREVIEW = 600;
  const bio = data.profile || '';
  const preview = bio.length > PREVIEW ? bio.slice(0, PREVIEW) + '…' : bio;
  const primaryImg = data.images.find(i => i.type === 'primary') || data.images[0];

  return (
    <div className="rounded-xl border border-border/60 bg-card/40 overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-500/8 border-b border-border/30">
        <Music className="h-4 w-4 text-amber-400 flex-shrink-0" />
        <span className="text-sm font-semibold flex-grow text-amber-200">Discogs</span>
        {data.dataQuality && (
          <span className="text-[10px] text-muted-foreground hidden sm:block">Quality: {data.dataQuality}</span>
        )}
        <a href={data.url} target="_blank" rel="noopener noreferrer" aria-label="Open on Discogs"
          className="text-xs text-muted-foreground hover:text-primary flex items-center gap-0.5 flex-shrink-0">
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>

      <div className="p-4 flex gap-4">
        {primaryImg && (
          <div className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border border-border/40">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={primaryImg.uri} alt={data.name} className="w-full h-full object-cover"
              onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          </div>
        )}
        <div className="flex-1 min-w-0 space-y-2">
          {bio ? (
            <>
              <p className="text-sm leading-relaxed text-foreground/85 whitespace-pre-line">{expanded ? bio : preview}</p>
              {bio.length > PREVIEW && (
                <Button variant="ghost" size="sm" className="h-6 text-xs text-primary px-0 hover:bg-transparent"
                  onClick={() => setExpanded(!expanded)}>
                  {expanded ? <><ChevronUp className="h-3 w-3 mr-1" />Show less</> : <><ChevronDown className="h-3 w-3 mr-1" />Read more</>}
                </Button>
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground italic">No biography on Discogs yet.</p>
          )}

          {/* Name variations */}
          {data.namevariations.length > 0 && (
            <p className="text-xs text-muted-foreground">
              <span className="font-medium">Name variations: </span>{data.namevariations.join(' · ')}
            </p>
          )}

          {/* Members */}
          {data.members && data.members.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-1">
              {data.members.map((m, i) => (
                <span key={i} className={`text-xs px-2 py-0.5 rounded-full border ${m.active ? 'border-amber-400/40 text-amber-300' : 'border-border/40 text-muted-foreground opacity-60'}`}>
                  {m.name}{!m.active ? ' (former)' : ''}
                </span>
              ))}
            </div>
          )}

          {/* Discogs URLs */}
          {data.urls.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {data.urls.slice(0, 12).map((u, i) => {
                const p = platformFromUrl(u);
                return (
                  <a key={i} href={u} target="_blank" rel="noopener noreferrer">
                    <Badge variant="outline" className={`cursor-pointer hover:bg-secondary transition-colors gap-1 text-xs ${p.color}`}>
                      <ExternalLink className="h-3 w-3" />{p.name}
                    </Badge>
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Card: Last.fm ─────────────────────────────────────────────────────────────

function LastFmCard({ data }: { data: LastFmInfo }) {
  const [expanded, setExpanded] = useState(false);
  const PREVIEW = 500;
  const bio = data.bio || '';
  const preview = bio.length > PREVIEW ? bio.slice(0, PREVIEW) + '…' : bio;

  return (
    <div className="rounded-xl border border-border/60 bg-card/40 overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 bg-red-500/8 border-b border-border/30">
        <Headphones className="h-4 w-4 text-red-400 flex-shrink-0" />
        <span className="text-sm font-semibold flex-grow text-red-200">Last.fm</span>
        <a href={data.url} target="_blank" rel="noopener noreferrer" aria-label="Open on Last.fm"
          className="text-xs text-muted-foreground hover:text-primary flex items-center gap-0.5 flex-shrink-0">
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>

      <div className="p-4 space-y-3">
        {/* Stats */}
        <div className="flex gap-6">
          <div>
            <p className="text-xs text-muted-foreground">Listeners</p>
            <p className="text-lg font-bold">{fmtNum(data.listeners)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Scrobbles</p>
            <p className="text-lg font-bold">{fmtNum(data.playcount)}</p>
          </div>
        </div>

        {/* Bio */}
        {bio && (
          <div>
            <p className="text-sm leading-relaxed text-foreground/85">{expanded ? bio : preview}</p>
            {bio.length > PREVIEW && (
              <Button variant="ghost" size="sm" className="h-6 text-xs text-primary px-0 hover:bg-transparent mt-1"
                onClick={() => setExpanded(!expanded)}>
                {expanded ? <><ChevronUp className="h-3 w-3 mr-1" />Show less</> : <><ChevronDown className="h-3 w-3 mr-1" />Read more</>}
              </Button>
            )}
          </div>
        )}

        {/* Tags */}
        {data.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {data.tags.map(t => (
              <Badge key={t} variant="secondary" className="text-[11px] h-5 font-normal">{t}</Badge>
            ))}
          </div>
        )}

        {/* Similar artists */}
        {data.similar.length > 0 && (
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">Similar Artists</p>
            <div className="flex flex-wrap gap-1.5">
              {data.similar.map((s, i) => (
                <a key={i} href={s.url} target="_blank" rel="noopener noreferrer">
                  <Badge variant="outline" className="cursor-pointer hover:bg-secondary transition-colors text-xs gap-1">
                    <ExternalLink className="h-3 w-3" />{s.name}
                  </Badge>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Dismissible wrapper ───────────────────────────────────────────────────────
// Renders any rich-info card with a small admin-only "Hide" pill in the top
// right. The pill calls onDismiss to persist a dismissal — the parent then
// stops rendering this card on future loads until restored from the banner.

function DismissibleCard({
  canManage,
  onDismiss,
  label,
  children,
}: {
  canManage: boolean;
  onDismiss: () => void;
  label: string;
  children: React.ReactNode;
}) {
  if (!canManage) return <>{children}</>;
  return (
    <div className="relative group">
      <button
        onClick={onDismiss}
        className="absolute top-2 right-2 z-20 bb-hover-reveal transition-opacity bg-destructive text-white hover:bg-destructive/80 rounded-full px-2 py-1 text-[10px] font-medium flex items-center gap-1 shadow-md"
        title={`${label} (admin only — scraped match was wrong)`}
        aria-label={label}
      >
        <EyeOff className="h-3 w-3" /> Hide
      </button>
      {children}
    </div>
  );
}

// ── Loading skeleton ──────────────────────────────────────────────────────────

function RichSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3].map(i => (
        <div key={i} className="rounded-xl border border-border/30 bg-card/30 p-4 space-y-2">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-5/6" />
          <div className="flex gap-1.5 pt-1">
            <Skeleton className="h-5 w-16 rounded-full" />
            <Skeleton className="h-5 w-20 rounded-full" />
            <Skeleton className="h-5 w-14 rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface WebResultsSectionProps {
  query: string;
  entityType?: string;
  entityName?: string;
  entityId?: string;
  entityCollection?: string;
  canManage?: boolean;
  /** When true, auto-scanned source cards (Wikipedia / Discogs / MusicBrainz /
   *  Last.fm) stay PRIVATE until the entity's owner explicitly confirms
   *  "yes, this is me". Used on user profiles — the web scan is fuzzy and a
   *  wrong match must never show publicly (Gordon: "user jona gold gets a
   *  link to jona Viray???"). */
  requireApproval?: boolean;
  className?: string;
}

export default function WebResultsSection({
  query,
  entityType = "artist",
  entityName,
  entityId,
  entityCollection = "artists",
  canManage = false,
  requireApproval = false,
  className = "",
}: WebResultsSectionProps) {
  const { toast } = useToast();

  // Rich data from /api/artist-info
  const [artistInfo, setArtistInfo] = useState<ArtistInfoData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Custom curated links (admin-managed, persisted in Firestore)
  const [customLinks, setCustomLinks] = useState<CustomWebLink[]>([]);
  const [customLinksLoaded, setCustomLinksLoaded] = useState(false);
  const [savingLinks, setSavingLinks] = useState(false);

  // Admin overrides for scraped / auto-fetched content:
  //   • dismissed — which source cards (wikipedia/discogs/musicbrainz/lastfm)
  //     are hidden because the scraper matched the wrong entity.
  //   • queryOverride — replaces the search term we send to /api/artist-info
  //     when the auto-derived "title + artist" matches the wrong record.
  const [dismissed, setDismissed] = useState<string[]>([]);
  // Sources the entity's owner has vetted as "yes, this is me" — only these
  // are ever shown publicly when requireApproval is on.
  const [approved, setApproved] = useState<string[]>([]);
  const [queryOverride, setQueryOverride] = useState<string>("");
  // Local draft for the override form (admins type here, hit Save to persist).
  const [overrideDraft, setOverrideDraft] = useState<string>("");
  const [showOverrideForm, setShowOverrideForm] = useState(false);

  // Add-link form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newLinkUrl, setNewLinkUrl] = useState("");
  const [newLinkTitle, setNewLinkTitle] = useState("");
  const [newLinkSnippet, setNewLinkSnippet] = useState("");

  // Edit-link form — curated links could only be added or deleted, never
  // edited (Gordon: "can't edit web info link??? can add and erase but not
  // edit???"), so fixing a typo meant delete + re-add from scratch.
  const [editingLinkId, setEditingLinkId] = useState<string | null>(null);
  const [editLinkUrl, setEditLinkUrl] = useState("");
  const [editLinkTitle, setEditLinkTitle] = useState("");
  const [editLinkSnippet, setEditLinkSnippet] = useState("");

  // Streaming panel toggle
  const [showStreamingPanel, setShowStreamingPanel] = useState(false);

  // Load curated links + admin overrides from Firestore
  useEffect(() => {
    if (!entityId || customLinksLoaded) return;
    getEntityWebLinks(entityCollection, entityId).then(({ links, dismissed: d, approved: a, queryOverride: q }) => {
      setCustomLinks(links);
      setDismissed(d);
      setApproved(a);
      setQueryOverride(q);
      setOverrideDraft(q);
      setCustomLinksLoaded(true);
    });
  }, [entityId, entityCollection, customLinksLoaded]);

  // Effective query — admin override takes priority over the auto-derived one.
  // Recomputed each render so a freshly-saved override is used on the very next
  // fetchInfo without waiting for state to settle.
  const effectiveQuery = (queryOverride || entityName || query || "").trim();

  // Auto-fetch rich artist info on mount AND any time the effective query
  // changes (admin saves an override → we re-fetch with the corrected term).
  useEffect(() => {
    if (effectiveQuery && customLinksLoaded) {
      // Re-fetch on every effective-query change after the initial Firestore
      // load. customLinksLoaded gates the very first fetch so we don't fire
      // twice with the wrong term on mount.
      setHasLoaded(false);
      fetchInfo();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveQuery, customLinksLoaded]);

  const fetchInfo = useCallback(async () => {
    if (!effectiveQuery) return;
    setIsLoading(true);
    setHasLoaded(true);
    try {
      const resp = await fetch(`/api/artist-info?name=${encodeURIComponent(effectiveQuery)}`);
      const data = await resp.json();
      setArtistInfo(data);
    } catch (err) {
      console.error('[WebResultsSection] fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [effectiveQuery]);

  // Curated-link CRUD — write-through preserves dismissed[] + queryOverride so
  // a curated-link save doesn't accidentally wipe an admin's earlier overrides.
  const saveLinks = async (links: CustomWebLink[]) => {
    if (!entityId) return;
    setSavingLinks(true);
    const ok = await saveEntityWebLinks(entityCollection, entityId, links, dismissed, queryOverride, approved);
    setSavingLinks(false);
    if (!ok) toast({ variant: "destructive", title: "Save failed", description: "Check your permissions." });
  };

  // Dismiss / restore an auto-fetched source card (wikipedia/discogs/etc.).
  const toggleDismissed = async (source: string) => {
    if (!entityId) return;
    const next = dismissed.includes(source)
      ? dismissed.filter(s => s !== source)
      : [...dismissed, source];
    setDismissed(next);
    setSavingLinks(true);
    const ok = await saveEntityWebLinks(entityCollection, entityId, customLinks, next, queryOverride, approved);
    setSavingLinks(false);
    if (!ok) toast({ variant: "destructive", title: "Save failed", description: "Check your permissions." });
  };

  // Owner vetting — "yes this is me" approves a source for public display;
  // "not me" both un-approves AND dismisses it so it stops appearing.
  const setApprovalFor = async (source: string, isMe: boolean) => {
    if (!entityId) return;
    const nextApproved = isMe
      ? [...new Set([...approved, source])]
      : approved.filter(s => s !== source);
    const nextDismissed = isMe
      ? dismissed.filter(s => s !== source)
      : [...new Set([...dismissed, source])];
    setApproved(nextApproved);
    setDismissed(nextDismissed);
    setSavingLinks(true);
    const ok = await saveEntityWebLinks(entityCollection, entityId, customLinks, nextDismissed, queryOverride, nextApproved);
    setSavingLinks(false);
    if (!ok) toast({ variant: "destructive", title: "Save failed", description: "Check your permissions." });
  };

  // Save the admin-typed search-query override. Empty string clears it.
  const saveQueryOverride = async () => {
    if (!entityId) return;
    const next = overrideDraft.trim();
    setQueryOverride(next);
    setSavingLinks(true);
    const ok = await saveEntityWebLinks(entityCollection, entityId, customLinks, dismissed, next, approved);
    setSavingLinks(false);
    setShowOverrideForm(false);
    if (!ok) toast({ variant: "destructive", title: "Save failed", description: "Check your permissions." });
    else toast({ title: next ? "Query override saved" : "Query override cleared", description: "Re-fetching web info…" });
  };

  const handleAddLink = async () => {
    // URL is the only true requirement — auto-derive a title from the
    // hostname when the user didn't bother. Per Gordon: "can't save Web
    // info link, save button greyed out" — the Title field's required
    // gate was the blocker.
    const rawUrl = newLinkUrl.trim();
    if (!rawUrl) return;
    let derivedTitle = newLinkTitle.trim();
    if (!derivedTitle) {
      try {
        const u = new URL(rawUrl.startsWith('http') ? rawUrl : `https://${rawUrl}`);
        derivedTitle = u.hostname.replace(/^www\./, '') + (u.pathname && u.pathname !== '/' ? u.pathname.replace(/\/$/, '') : '');
      } catch {
        derivedTitle = rawUrl;
      }
    }
    const link: CustomWebLink = {
      id: Date.now().toString(),
      title: derivedTitle,
      url: rawUrl,
      snippet: newLinkSnippet.trim() || undefined,
      addedAt: new Date().toISOString(),
    };
    const updated = [...customLinks, link];
    setCustomLinks(updated);
    setNewLinkUrl(""); setNewLinkTitle(""); setNewLinkSnippet("");
    setShowAddForm(false);
    await saveLinks(updated);
  };

  const handleDeleteLink = async (id: string) => {
    const updated = customLinks.filter(l => l.id !== id);
    setCustomLinks(updated);
    await saveLinks(updated);
  };

  const startEditLink = (link: CustomWebLink) => {
    setEditingLinkId(link.id);
    setEditLinkUrl(link.url);
    setEditLinkTitle(link.title);
    setEditLinkSnippet(link.snippet || "");
  };

  const cancelEditLink = () => setEditingLinkId(null);

  const handleUpdateLink = async () => {
    if (!editingLinkId) return;
    const rawUrl = editLinkUrl.trim();
    if (!rawUrl) return;
    let derivedTitle = editLinkTitle.trim();
    if (!derivedTitle) {
      try {
        const u = new URL(rawUrl.startsWith('http') ? rawUrl : `https://${rawUrl}`);
        derivedTitle = u.hostname.replace(/^www\./, '') + (u.pathname && u.pathname !== '/' ? u.pathname.replace(/\/$/, '') : '');
      } catch {
        derivedTitle = rawUrl;
      }
    }
    const updated = customLinks.map(l => l.id === editingLinkId
      ? { ...l, url: rawUrl, title: derivedTitle, snippet: editLinkSnippet.trim() || undefined }
      : l);
    setCustomLinks(updated);
    setEditingLinkId(null);
    await saveLinks(updated);
  };

  const encodedQ = encodeURIComponent(`${query} ${entityType === "artist" ? "musician" : ""}`);
  const encodedName = encodeURIComponent(query);

  const streamingPlatforms = [
    { label: "Spotify",      url: `https://open.spotify.com/search/${encodedName}`,   color: "text-green-500" },
    { label: "Apple Music",  url: `https://music.apple.com/search?term=${encodedName}`, color: "text-pink-500" },
    { label: "YouTube",      url: `https://www.youtube.com/results?search_query=${encodedName}+music`, color: "text-red-500" },
    { label: "SoundCloud",   url: `https://soundcloud.com/search?q=${encodedName}`,   color: "text-orange-500" },
    { label: "Bandcamp",     url: `https://bandcamp.com/search?q=${encodedName}`,     color: "text-teal-500" },
    { label: "Tidal",        url: `https://tidal.com/browse/search?q=${encodedName}`, color: "text-cyan-500" },
    { label: "Deezer",       url: `https://www.deezer.com/search/${encodedName}`,     color: "text-purple-500" },
    { label: "Amazon Music", url: `https://music.amazon.com/search/${encodedName}`,   color: "text-yellow-500" },
    { label: "Beatport",     url: `https://www.beatport.com/search?q=${encodedName}`, color: "text-emerald-500" },
    { label: "AllMusic",     url: `https://www.allmusic.com/search/all/${encodedName}`, color: "text-blue-400" },
    { label: "Discogs",      url: `https://www.discogs.com/search?q=${encodedName}&type=artist`, color: "text-amber-500" },
    { label: "MusicBrainz",  url: `https://musicbrainz.org/search?query=${encodedName}&type=artist`, color: "text-violet-500" },
    { label: "Genius",       url: `https://genius.com/search?q=${encodedName}`,       color: "text-yellow-300" },
    { label: "Last.fm",      url: `https://www.last.fm/search?q=${encodedName}`,      color: "text-red-400" },
    { label: "Rate Your Music", url: `https://rateyourmusic.com/search?searchterm=${encodedName}&searchtype=A`, color: "text-sky-400" },
  ];

  const hasAnyRichData = artistInfo && (
    artistInfo.wikipedia?.found ||
    artistInfo.musicbrainz?.found ||
    artistInfo.discogs?.found ||
    artistInfo.lastfm?.found
  );

  return (
    <div className={`mt-10 ${className}`}>
      {/* ── Header ── */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Globe className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold">Web Info</h2>
          {artistInfo?.musicbrainz?.found && (
            <Badge variant="outline" className="text-[10px] gap-1 text-violet-400 border-violet-400/30">
              MusicBrainz
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canManage && (
            <>
              <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs"
                onClick={() => setShowAddForm(!showAddForm)}>
                {showAddForm ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                {showAddForm ? "Cancel" : "Add Link"}
              </Button>
              {/* Fix Match — overrides the search term used to fetch Wikipedia /
                  Discogs / MusicBrainz / Last.fm when the auto-derived
                  "title + artist" matched the wrong record. */}
              <Button
                size="sm"
                variant={queryOverride ? "default" : "outline"}
                className={`h-8 gap-1.5 text-xs ${queryOverride ? "bg-amber-500 text-black hover:bg-amber-400" : ""}`}
                onClick={() => { setOverrideDraft(queryOverride); setShowOverrideForm(s => !s); }}
                title={queryOverride ? `Currently overridden to: ${queryOverride}` : "Fix wrong web-info match"}
              >
                <Search className="h-3.5 w-3.5" />
                {queryOverride ? "Match Fixed" : "Fix Match"}
              </Button>
            </>
          )}
          <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs"
            onClick={() => { setHasLoaded(false); fetchInfo(); }} disabled={isLoading}>
            <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
            {isLoading ? "Loading…" : "Refresh"}
          </Button>
        </div>
      </div>

      {/* ── Admin: Query override form ── */}
      {canManage && showOverrideForm && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 space-y-2 mb-4">
          <p className="text-xs font-medium text-amber-300 flex items-center gap-1.5">
            <Search className="h-3.5 w-3.5" /> Override the search term
          </p>
          <p className="text-[11px] text-muted-foreground">
            Used when the auto-detected term (currently "<span className="font-mono">{entityName || query}</span>")
            pulls in the wrong Wikipedia / Discogs / MusicBrainz / Last.fm record.
            Leave blank and Save to clear the override.
          </p>
          <Input
            placeholder='e.g. "Leafnuts Jamaican band"'
            value={overrideDraft}
            onChange={e => setOverrideDraft(e.target.value)}
            className="h-8 text-sm"
          />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" className="h-7 text-xs"
              onClick={() => { setOverrideDraft(queryOverride); setShowOverrideForm(false); }}>
              Cancel
            </Button>
            {queryOverride && (
              <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-destructive border-destructive/40 hover:bg-destructive/10"
                onClick={() => { setOverrideDraft(""); saveQueryOverride(); }}>
                <Trash2 className="h-3.5 w-3.5" /> Clear Override
              </Button>
            )}
            <Button size="sm" className="h-7 text-xs gap-1" onClick={saveQueryOverride}>
              <Check className="h-3.5 w-3.5" /> Save & Re-fetch
            </Button>
          </div>
        </div>
      )}

      {/* ── Dismissed-cards restore banner ── */}
      {canManage && dismissed.length > 0 && (
        <div className="rounded-lg border border-border/50 bg-muted/30 p-2.5 mb-4 flex items-center gap-2 flex-wrap">
          <EyeOff className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">Hidden:</span>
          {dismissed.map(src => (
            <Button key={src} size="sm" variant="outline" className="h-6 text-[11px] gap-1 capitalize"
              onClick={() => toggleDismissed(src)} title={`Restore ${src} card`}>
              <Eye className="h-3 w-3" /> {src}
            </Button>
          ))}
        </div>
      )}

      <div className="space-y-4">

        {/* ── Add-link form ── */}
        {canManage && showAddForm && (
          <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2">
            <p className="text-xs font-medium text-muted-foreground">Add a curated link</p>
            <Input placeholder="URL (https://…)" value={newLinkUrl} onChange={e => setNewLinkUrl(e.target.value)} className="h-8 text-sm" />
            <Input placeholder="Title" value={newLinkTitle} onChange={e => setNewLinkTitle(e.target.value)} className="h-8 text-sm" />
            <Textarea placeholder="Description (optional)" value={newLinkSnippet} onChange={e => setNewLinkSnippet(e.target.value)} rows={2} className="text-sm resize-none" />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setShowAddForm(false)}>Cancel</Button>
              <Button size="sm" className="h-7 text-xs gap-1" disabled={!newLinkUrl.trim()} onClick={handleAddLink}>
                <Check className="h-3.5 w-3.5" /> Save
              </Button>
            </div>
          </div>
        )}

        {/* ── Curated links ── */}
        {customLinks.length > 0 && (
          <div className="rounded-xl border border-border/50 bg-card/30 p-4 space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1.5 mb-2">
              <Link2 className="h-3.5 w-3.5" /> Curated Links
            </p>
            {customLinks.map(link => (
              editingLinkId === link.id ? (
                <div key={link.id} className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2">
                  <Input placeholder="URL (https://…)" value={editLinkUrl} onChange={e => setEditLinkUrl(e.target.value)} className="h-8 text-sm" />
                  <Input placeholder="Title" value={editLinkTitle} onChange={e => setEditLinkTitle(e.target.value)} className="h-8 text-sm" />
                  <Textarea placeholder="Description (optional)" value={editLinkSnippet} onChange={e => setEditLinkSnippet(e.target.value)} rows={2} className="text-sm resize-none" />
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={cancelEditLink}>Cancel</Button>
                    <Button size="sm" className="h-7 text-xs gap-1" disabled={!editLinkUrl.trim()} onClick={handleUpdateLink}>
                      <Check className="h-3.5 w-3.5" /> Save
                    </Button>
                  </div>
                </div>
              ) : (
                <div key={link.id} className="flex items-start gap-2 group rounded-lg hover:bg-secondary/30 p-2 transition-colors">
                  <Globe className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div className="flex-grow min-w-0">
                    <a href={link.url} target="_blank" rel="noopener noreferrer"
                      className="text-sm font-medium hover:text-primary transition-colors line-clamp-1">{link.title}</a>
                    <p className="text-xs text-green-600 dark:text-green-400 truncate">
                      {link.url.replace(/^https?:\/\/(www\.)?/, '').split('/')[0]}
                    </p>
                    {link.snippet && <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{link.snippet}</p>}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <a href={link.url} target="_blank" rel="noopener noreferrer" aria-label={`Open ${link.title}`}
                      className="bb-hover-reveal focus-visible:opacity-100 text-muted-foreground hover:text-primary transition-all">
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                    {canManage && (
                      <>
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-primary hover:bg-primary/10"
                          aria-label={`Edit ${link.title}`}
                          onClick={() => startEditLink(link)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive hover:bg-destructive/10"
                          aria-label={`Delete ${link.title}`}
                          onClick={() => handleDeleteLink(link.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )
            ))}
          </div>
        )}

        {/* ── Rich auto-fetched data ──
            Each source card is wrapped in a relative div so admins can hover
            and hit a small "Hide" pill to dismiss it (e.g. the scraper matched
            the wrong band on Wikipedia). Dismissals persist to Firestore via
            webLinksDismissed[] and the card is filtered out on next load. */}
        {isLoading ? (
          <RichSkeleton />
        ) : hasAnyRichData ? (
          <>
            {([
              ['wikipedia',   'Wikipedia',   artistInfo!.wikipedia?.found   ? <WikipediaCard data={artistInfo!.wikipedia!} />     : null],
              ['musicbrainz', 'MusicBrainz', artistInfo!.musicbrainz?.found ? <MusicBrainzCard data={artistInfo!.musicbrainz!} /> : null],
              ['discogs',     'Discogs',     artistInfo!.discogs?.found     ? <DiscogsCard data={artistInfo!.discogs!} />         : null],
              ['lastfm',      'Last.fm',     artistInfo!.lastfm?.found      ? <LastFmCard data={artistInfo!.lastfm!} />           : null],
            ] as [string, string, React.ReactNode][]).map(([source, label, card]) => {
              if (!card || dismissed.includes(source)) return null;
              const isApproved = approved.includes(source);
              // Vetting gate: unapproved scans are OWNER-ONLY, wrapped in an
              // "Is this you?" banner. Visitors never see an unvetted match.
              if (requireApproval && !isApproved) {
                if (!canManage) return null;
                return (
                  <div key={source} className="rounded-xl border-2 border-amber-500/70 overflow-hidden">
                    <div className="flex items-center gap-2 flex-wrap px-3 py-2 bg-amber-500/10 border-b border-amber-500/40">
                      <Search className="h-4 w-4 text-amber-400 flex-shrink-0" />
                      <p className="text-xs text-foreground flex-grow min-w-[150px]">
                        <span className="font-semibold">Found on {label} — is this you?</span>{' '}
                        <span className="text-muted-foreground">Only you can see this until you confirm it.</span>
                      </p>
                      <Button size="sm" className="h-7 text-xs gap-1 bg-emerald-600 text-white hover:bg-emerald-500"
                        onClick={() => setApprovalFor(source, true)}>
                        <Check className="h-3.5 w-3.5" /> Yes, this is me
                      </Button>
                      <Button size="sm" variant="outline"
                        className="h-7 text-xs gap-1 border-2 border-destructive/60 text-destructive hover:bg-destructive/10"
                        onClick={() => setApprovalFor(source, false)}>
                        <X className="h-3.5 w-3.5" /> Not me
                      </Button>
                    </div>
                    {card}
                  </div>
                );
              }
              return (
                <DismissibleCard key={source} canManage={canManage} onDismiss={() => toggleDismissed(source)} label={`Hide ${label}`}>
                  {requireApproval && canManage ? (
                    <div className="relative">
                      <span className="absolute top-2 left-2 z-10 flex items-center gap-1 rounded-full bg-emerald-600 text-white text-[10px] font-medium px-2 py-0.5 shadow">
                        <Check className="h-3 w-3" /> Vetted by you
                      </span>
                      {card}
                    </div>
                  ) : card}
                </DismissibleCard>
              );
            })}
          </>
        ) : hasLoaded ? (
          <div className="text-center py-8 text-muted-foreground space-y-2">
            <Globe className="h-8 w-8 mx-auto opacity-30" />
            <p className="text-sm">No web results found for "{entityName || query}"</p>
            <p className="text-xs opacity-60">Try searching manually below or add a curated link.</p>
          </div>
        ) : null}

        {/* ── Search externally ── */}
        <div className="pt-1 border-t border-border/30">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-muted-foreground">Search externally</p>
            <Button size="sm" variant="ghost" className="h-6 text-xs gap-1 text-muted-foreground hover:text-foreground"
              onClick={() => setShowStreamingPanel(!showStreamingPanel)}>
              <Music className="h-3 w-3" /> Platforms
              {showStreamingPanel ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </Button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            <a href={`https://www.google.com/search?q=${encodedQ}`} target="_blank" rel="noopener noreferrer">
              <Badge variant="outline" className="cursor-pointer hover:bg-secondary transition-colors gap-1 text-xs">
                <ExternalLink className="h-3 w-3" /> Google
              </Badge>
            </a>
            <a href={`https://en.wikipedia.org/w/index.php?search=${encodedName}`} target="_blank" rel="noopener noreferrer">
              <Badge variant="outline" className="cursor-pointer hover:bg-secondary transition-colors gap-1 text-xs">
                <BookOpen className="h-3 w-3" /> Wikipedia
              </Badge>
            </a>
            <a href={`https://www.discogs.com/search?q=${encodedName}&type=artist`} target="_blank" rel="noopener noreferrer">
              <Badge variant="outline" className="cursor-pointer hover:bg-secondary text-amber-400 border-amber-400/30 transition-colors gap-1 text-xs">
                <ExternalLink className="h-3 w-3" /> Discogs
              </Badge>
            </a>
            <a href={`https://musicbrainz.org/search?query=${encodedName}&type=artist`} target="_blank" rel="noopener noreferrer">
              <Badge variant="outline" className="cursor-pointer hover:bg-secondary text-violet-400 border-violet-400/30 transition-colors gap-1 text-xs">
                <ExternalLink className="h-3 w-3" /> MusicBrainz
              </Badge>
            </a>
          </div>
          {showStreamingPanel && (
            <div className="mt-2 rounded-lg border border-border/40 bg-secondary/20 p-2.5">
              <div className="flex flex-wrap gap-1.5">
                {streamingPlatforms.map(p => (
                  <a key={p.label} href={p.url} target="_blank" rel="noopener noreferrer">
                    <Badge variant="outline" className={`cursor-pointer hover:bg-secondary transition-colors gap-1 text-xs ${p.color}`}>
                      <ExternalLink className="h-3 w-3" /> {p.label}
                    </Badge>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        {savingLinks && <p className="text-xs text-muted-foreground text-right animate-pulse">Saving…</p>}
      </div>
    </div>
  );
}
